import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Inbox, Sparkles, Eye, Send, Check, Languages, Workflow, ShieldCheck, Database, Plus } from 'lucide-react';
import Button from '../components/common/Button';
import TradeTicketVisual from '../components/home/TradeTicketVisual';

const ease = [0.16, 1, 0.3, 1];

const PIPELINE = [
  { icon: Inbox, title: 'Capture', body: 'Every confirmation that lands in your inbox — recorded-call transcripts, forwarded threads, attachments — is read in real time.' },
  { icon: Sparkles, title: 'Extract', body: 'Domain models pull every field. Confidence-scored. Cross-checked against counterparty history and master data.' },
  { icon: Eye, title: 'Verify', body: 'Side-by-side ticket. Ambiguous fields are highlighted, never silently filled. Your trader is the final word.' },
  { icon: Send, title: 'Post', body: 'One click books in your TRM. Failed posts come back with a reason, not a stack trace.' },
];

const COMMODITIES = [
  { name: 'Crude oil',          examples: 'Qatar Marine · Al-Shaheen · Qatar Land · Arab Light · Arab Medium · Arab Heavy · Arab Extra Light · Murban · Das · Upper Zakum' },
  { name: 'Refined products',   examples: 'Naphtha · Gasoil · Jet · Fuel oil · LPG' },
  { name: 'LNG',                examples: 'QatarEnergy LNG term & spot · DES · FOB cargoes' },
  { name: 'Petrochemicals',     examples: 'MEG · Paraxylene · Benzene · Methanol' },
];

const TRMS = ['Allegro', 'Openlink Endur', 'RightAngle', 'Eka', 'Aspect', 'SAP CTRM', 'Custom'];

const FAQ = [
  {
    q: 'How accurate is the field extraction?',
    a: "Core Trade is purpose-built for the kind of confirmations Gulf desks actually receive — bilingual, multi-counterparty, threaded over days. Every field comes with a confidence score, and anything below your configured threshold is flagged for review. The system never silently fills a field it isn't sure about. In a demo we'll run it against a redacted sample of your own threads so you can judge for yourself.",
  },
  {
    q: 'Will this work with our existing TRM?',
    a: "We're building toward native connectors for Allegro, Openlink Endur, RightAngle, Eka, Aspect and SAP CTRM — the systems we see most often in GCC desks. For in-house and bespoke TRMs we scope a custom connector during onboarding. If you can tell us what you use, we can tell you exactly what integration looks like.",
  },
  {
    q: 'Is our data used to train your models?',
    a: "Never. Models are tuned on public market data and synthetic confirmations. Customer trades are isolated per-tenant and used only to serve that tenant's predictions. We can document this in writing for your compliance team.",
  },
  {
    q: "What's the rollout look like?",
    a: "Onboarding is designed around a two-week shape: week one is connectors, master data sync and security review; week two is shadow mode, where Core Trade drafts tickets your team reviews without committing. Go-live happens when your head trader signs off — not before.",
  },
  {
    q: "Are you live with any customers yet?",
    a: "Not yet. We're pre-launch and currently scheduling product demos with desks across Doha, Riyadh, Dubai and Abu Dhabi. Early customers get founder-led onboarding and pricing locked for the first 12 months.",
  },
];

const ProductPage = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-radial-amber pointer-events-none" />
        <div className="absolute inset-0 grid-bg pointer-events-none" />

        <div className="container-prose relative">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
            className="text-[11px] font-semibold tracking-[0.16em] uppercase text-amber-light mb-5 text-center"
          >
            The product
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.05, ease }}
            className="text-hero font-bold text-canvas-white text-center text-balance max-w-4xl mx-auto"
          >
            One workflow.{' '}
            <span className="bg-gradient-to-r from-amber via-amber-light to-amber bg-clip-text text-transparent">
              Every confirmation.
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.18, ease }}
            className="mt-6 text-[17px] leading-relaxed text-ink-faint text-center max-w-2xl mx-auto text-balance"
          >
            Core Trade replaces the manual re-typing step between "trade agreed" and
            "trade booked." Here's exactly how that works.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.4, ease }}
            className="mt-16"
          >
            <TradeTicketVisual />
          </motion.div>
        </div>
      </section>

      {/* Pipeline */}
      <section className="py-28 bg-canvas-dark border-t border-white/[0.05]">
        <div className="container-prose">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5">The pipeline</p>
            <h2 className="text-display font-bold text-canvas-white text-balance">
              Four stages.{' '}
              <span className="text-ink-faint">Every one observable, every one auditable.</span>
            </h2>
          </div>

          <div className="mt-16 relative">
            <div className="absolute top-6 left-0 right-0 hidden md:block pointer-events-none">
              <div className="h-px bg-gradient-to-r from-transparent via-amber/30 to-transparent" />
            </div>
            <div className="grid md:grid-cols-4 gap-5 relative">
              {PIPELINE.map(({ icon: Icon, title, body }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.1, ease }}
                >
                  <div className="w-12 h-12 rounded-2xl bg-canvas-surface border border-white/[0.08] flex items-center justify-center mb-5 relative z-10">
                    <Icon size={18} className="text-amber" strokeWidth={1.6} />
                  </div>
                  <p className="mono-tabular text-[10.5px] text-ink-faint mb-2">0{i + 1}</p>
                  <h3 className="text-[17px] font-semibold text-canvas-white tracking-tight mb-2.5">{title}</h3>
                  <p className="text-[13.5px] leading-relaxed text-ink-faint">{body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-28 bg-canvas-black">
        <div className="container-prose">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5">Coverage</p>
              <h2 className="text-display font-bold text-canvas-white text-balance">
                Built for Gulf physical trading.
              </h2>
              <p className="mt-6 text-[15px] leading-relaxed text-ink-faint max-w-md">
                Core Trade isn't a generic data extractor. We've trained the system on
                the specific confirmations, counterparties and contract structures that
                define commodity trading in the GCC.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
              {COMMODITIES.map((c) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, ease }}
                  className="bg-canvas-black p-7"
                >
                  <h3 className="text-[15.5px] font-semibold text-canvas-white tracking-tight mb-2 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber" />
                    {c.name}
                  </h3>
                  <p className="text-[12.5px] leading-relaxed text-ink-faint">{c.examples}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities deep-dive */}
      <section className="py-28 bg-canvas-dark">
        <div className="container-prose space-y-24">
          <FeatureRow
            icon={Languages}
            label="Bilingual extraction"
            title="English. Arabic. Recorded calls. Forwarded threads."
            body="GCC desks don't communicate in clean templates. Confirmations come in mixed scripts, contain transliterations, and often reference earlier recorded calls. Core Trade is built to handle all of it."
            capabilities={[
              { v: 'EN + AR',   l: 'across script and language' },
              { v: 'Call audio', l: 'recorded-call transcripts, attachments, forwards' },
              { v: 'Free-form', l: 'no template required from counterparty' },
            ]}
            note="Built specifically for Gulf physical trading — not adapted from a generic extractor."
          />
          <FeatureRow
            reverse
            icon={Workflow}
            label="TRM connectors"
            title="Posts to the system you already use."
            body="Native connectors — not RPA. Designed to track vendor releases, so when Allegro, Endur, RightAngle, Eka or SAP CTRM ship a change, the integration moves with them. Your team doesn't notice."
            capabilities={[
              { v: 'Native',     l: 'first-class connectors, not screen-scraping' },
              { v: 'Maintained', l: 'we track vendor releases for you' },
              { v: 'Custom',     l: 'in-house TRMs supported via scoped build' },
            ]}
            note="Tell us your TRM and we'll tell you exactly what integration looks like."
          />
          <FeatureRow
            icon={ShieldCheck}
            label="Audit & compliance"
            title="Every field, attributed. Every change, tracked."
            body="When a trade is questioned weeks later, you can reconstruct exactly who saw what, who edited what, and what the source said. Built for compliance, useful in disputes."
            capabilities={[
              { v: 'Attributed',  l: 'every edit and approver, timestamped' },
              { v: 'Retained',    l: 'long-horizon audit storage, exportable' },
              { v: 'SSO + RBAC',  l: 'SAML, SCIM, role-aware permissions' },
            ]}
            note="Designed around the kind of audit review compliance teams actually run."
          />
        </div>
      </section>

      {/* TRM logos */}
      <section className="py-20 bg-canvas-black">
        <div className="container-prose">
          <p className="text-center text-[10.5px] font-semibold tracking-[0.16em] uppercase text-ink-faint/70 mb-3">
            Built to integrate with
          </p>
          <p className="text-center text-[12.5px] text-ink-faint/80 mb-10 max-w-md mx-auto">
            The TRM and booking systems we see most often in GCC desks. Don't see yours? Tell us at the demo.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {TRMS.map((t) => (
              <div
                key={t}
                className="px-5 py-2.5 rounded-full bg-white/[0.03] ring-1 ring-white/[0.07] text-[13px] text-ink-faint hover:text-canvas-white hover:ring-white/[0.15] transition-colors flex items-center gap-2"
              >
                <Database size={12} className="text-amber" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-28 bg-canvas-dark">
        <div className="container-prose max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5">Common questions</p>
          <h2 className="text-display font-bold text-canvas-white text-balance mb-12">
            What desks always ask.
          </h2>
          <div className="divide-y divide-white/[0.06] border-y border-white/[0.06]">
            {FAQ.map((item, i) => (
              <FaqItem key={item.q} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-canvas-black border-t border-white/[0.05]">
        <div className="container-prose text-center max-w-2xl">
          <h2 className="text-display font-bold text-canvas-white text-balance">
            See it on your own threads.
          </h2>
          <p className="mt-6 text-[16px] leading-relaxed text-ink-faint">
            Bring a handful of redacted confirmations to a 30-minute demo. You'll see
            exactly what your team's day looks like with the manual step removed —
            before you commit to anything.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Button to="/request-demo" variant="primary" size="lg" withArrow>
              Book a demo
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left py-6 group"
      >
        <span className="text-[16px] font-medium text-canvas-white tracking-tight pr-4">
          {item.q}
        </span>
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full border border-white/[0.1] flex items-center justify-center text-ink-faint group-hover:text-canvas-white group-hover:border-white/25 transition-all duration-300 ${
            open ? 'rotate-45 border-amber/40 text-amber' : ''
          }`}
        >
          <Plus size={14} strokeWidth={2} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
              opacity: { duration: 0.22, ease: 'easeOut' },
            }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-[14.5px] leading-relaxed text-ink-faint max-w-2xl">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FeatureRow = ({ icon: Icon, label, title, body, capabilities, note, reverse = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.7, ease }}
    className="grid lg:grid-cols-12 gap-10 items-center"
  >
    <div className={`lg:col-span-5 ${reverse ? 'lg:order-2' : ''}`}>
      <div className="inline-flex items-center gap-2 mb-5">
        <Icon size={15} className="text-amber" strokeWidth={1.8} />
        <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light">{label}</span>
      </div>
      <h3 className="text-[28px] md:text-[34px] font-bold tracking-tight text-canvas-white text-balance leading-[1.15]">{title}</h3>
      <p className="mt-5 text-[15px] leading-relaxed text-ink-faint max-w-md">{body}</p>
    </div>
    <div className={`lg:col-span-7 ${reverse ? 'lg:order-1' : ''}`}>
      <div className="grid sm:grid-cols-3 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
        {capabilities.map((c) => (
          <div key={c.l} className="bg-canvas-surface p-7">
            <p className="text-[20px] font-semibold text-canvas-white tracking-tight">{c.v}</p>
            <p className="mt-2 text-[12.5px] leading-relaxed text-ink-faint">{c.l}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-2 text-[12px] text-ink-faint">
        <Check size={12} className="text-success flex-shrink-0" />
        {note}
      </div>
    </div>
  </motion.div>
);

export default ProductPage;
